import React from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { usePolledRequest } from "../hooks/usePolledRequest";
import { useGameClock } from "../hooks/useGameClock";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import {
  getGameUrl,
  getGoalsUrl,
  getPenaltiesUrl,
  getSparesUrl,
  getGamePlayerStatsUrl,
} from "../config/api";
import {
  Game,
  Goal,
  Penalty,
  Spare,
  GamePlayerStat,
  GameGoalieStat,
} from "../types/models";
import { PaginatedResponse } from "../types/api";
import TeamDisplay from "../Common/TeamDisplay";
import { formatGameDateTimeShort } from "../utils/dateUtils";
import {
  formatClock,
  periodLabel,
  regulationPeriodsOf,
} from "../utils/gameClock";
import LiveRefreshIndicator, { LIVE_REFRESH_MS } from "./LiveRefreshIndicator";

// Helper to format period for display
function formatPeriod(period: "period1" | "period2" | "period3"): string {
  return period === "period1" ? "1st" : period === "period2" ? "2nd" : "3rd";
}

// Helper to format time - prefers formatted field, falls back to parsing
function formatTime(
  time: string | null | undefined,
  formattedTime?: string | null,
): string {
  // Prefer the formatted time if available
  if (formattedTime) return formattedTime;

  if (!time) return "0:00";

  // If it's already in MM:SS format, return as-is
  if (/^\d{1,2}:\d{2}$/.test(time)) {
    return time;
  }

  // Parse ISO date format (e.g., "2000-01-01T00:12:34.000Z" or "2000-01-01T00:12:34")
  // Extract the time portion (HH:MM:SS) and convert to MM:SS
  const isoMatch = time.match(/T(\d{2}):(\d{2}):(\d{2})/);
  if (isoMatch) {
    const hours = parseInt(isoMatch[1], 10);
    const minutes = parseInt(isoMatch[2], 10);
    const seconds = parseInt(isoMatch[3], 10);
    // Convert total time to MM:SS format
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes}:${seconds.toString().padStart(2, "0")}`;
  }

  // Fallback: try to parse as Date and extract time
  try {
    const date = new Date(time);
    if (!isNaN(date.getTime())) {
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const seconds = date.getUTCSeconds();
      const totalMinutes = hours * 60 + minutes;
      return `${totalMinutes}:${seconds.toString().padStart(2, "0")}`;
    }
  } catch (e) {
    // Fall through to return original
  }

  return time;
}

// Helper to get penalty type label - prefers name over abbreviation
function getPenaltyTypeLabel(
  name?: string | null,
  abbr?: string | null,
): string {
  if (name) return name;
  if (abbr) return abbr;
  return "";
}

export default function GameDetailsPage() {
  const { gameId, abbr } = useParams<{ gameId: string; abbr: string }>();
  const auth = useAuth();

  const gameIdNum = parseInt(gameId || "0");

  const { setPageDetail } = useLeaguePublicContext();
  const { currentLeague } = useLeague();

  // Whether to keep reading can only be known from the game itself, so the
  // first read happens with no interval and the repeat starts once that read
  // says the game is live. A finished game's page therefore reads once and
  // stops, which is how most of these pages are looked at.
  const [isLiveGame, setIsLiveGame] = React.useState(false);
  const liveInterval = isLiveGame ? LIVE_REFRESH_MS : null;

  const { data: game, isRefreshing } = usePolledRequest<Game>(
    getGameUrl(gameIdNum),
    auth.token,
    liveInterval,
  );

  React.useEffect(() => {
    setIsLiveGame(game?.state === "playing");
  }, [game?.state]);

  // Ticks between reads, so the clock reads like a clock rather than jumping
  // every half minute. Safe before the game has loaded.
  const clockSeconds = useGameClock(game);

  // Put the matchup in the page title once the game loads.
  React.useEffect(() => {
    if (!game) return;

    setPageDetail(
      `${game.home_team_name ?? "Home"} vs ${
        game.visitor_team_name ?? "Visitor"
      }`,
    );
  }, [game, setPageDetail]);

  // The goals and penalties sit beside the score on this page, so they refresh
  // with it: a goal must never bump the score without appearing in the list
  // below it.
  const { data: goalsResponse } = usePolledRequest<PaginatedResponse<Goal>>(
    getGoalsUrl(gameIdNum),
    auth.token,
    liveInterval,
  );

  const { data: penaltiesResponse } = usePolledRequest<
    PaginatedResponse<Penalty>
  >(getPenaltiesUrl(gameIdNum), auth.token, liveInterval);

  const { data: sparesResponse } = useRequest<PaginatedResponse<Spare>>(
    getSparesUrl(gameIdNum),
    auth.token,
  );

  const { data: playerStats } = useRequest<GamePlayerStat[]>(
    getGamePlayerStatsUrl(gameIdNum),
    auth.token,
  );

  const { data: goalieStats } = useRequest<GameGoalieStat[]>(
    getGamePlayerStatsUrl(gameIdNum, "goalie"),
    auth.token,
  );

  const goals = goalsResponse?.data || [];
  const penalties = penaltiesResponse?.data || [];
  const spares = sparesResponse?.data || [];

  if (!game) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  const homeGoals = goals.filter((g) => g.team_id === game.home_team_id);
  const visitorGoals = goals.filter((g) => g.team_id === game.visitor_team_id);
  const homePenalties = penalties.filter(
    (p) => p.team_id === game.home_team_id,
  );
  const visitorPenalties = penalties.filter(
    (p) => p.team_id === game.visitor_team_id,
  );
  const homeSpares = spares.filter((s) => s.team_id === game.home_team_id);
  const visitorSpares = spares.filter(
    (s) => s.team_id === game.visitor_team_id,
  );

  // Split player stats by team and sort by jersey number
  const visitorPlayerStats = (playerStats || [])
    .filter((p) => p.team_id === game.visitor_team_id)
    .sort((a, b) => {
      const aNum = parseInt(a.player_jersey || "999");
      const bNum = parseInt(b.player_jersey || "999");
      return aNum - bNum;
    });

  const homePlayerStats = (playerStats || [])
    .filter((p) => p.team_id === game.home_team_id)
    .sort((a, b) => {
      const aNum = parseInt(a.player_jersey || "999");
      const bNum = parseInt(b.player_jersey || "999");
      return aNum - bNum;
    });

  // Split goalie stats by team and sort by jersey number
  const visitorGoalieStats = (goalieStats || [])
    .filter((g) => g.team_id === game.visitor_team_id)
    .sort((a, b) => {
      const aNum = parseInt(a.player_jersey || "999");
      const bNum = parseInt(b.player_jersey || "999");
      return aNum - bNum;
    });

  const homeGoalieStats = (goalieStats || [])
    .filter((g) => g.team_id === game.home_team_id)
    .sort((a, b) => {
      const aNum = parseInt(a.player_jersey || "999");
      const bNum = parseInt(b.player_jersey || "999");
      return aNum - bNum;
    });

  const isCompleted = game.state === "completed";
  const isLive = game.state === "playing";
  const isScheduled = game.state === "scheduled";
  const homeWon =
    isCompleted && (game.home_score ?? 0) > (game.visitor_score ?? 0);
  const visitorWon =
    isCompleted && (game.visitor_score ?? 0) > (game.home_score ?? 0);

  return (
    <div>
      {/* Game Header */}
      <div className="lp-card mb-3">
        <div className="lp-gamehead">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div style={{ flex: 1 }}>
              <div>
                <Link
                  to={`/leagues/${abbr}/teams/${game.visitor_team_id}`}
                  style={{
                    fontSize: "20px",
                    fontWeight: visitorWon ? 700 : 500,
                    color: "var(--text-primary)",
                    textDecoration: "none",
                  }}
                >
                  <TeamDisplay
                    team={{
                      id: game.visitor_team_id,
                      name: game.visitor_team_name || "Visitor Team",
                      colour_code: game.visitor_team_colour_code,
                    }}
                  />
                </Link>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-muted)",
                    marginTop: "2px",
                  }}
                >
                  Away
                </div>
              </div>
            </div>
            {(isCompleted || isLive) && (
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: isCompleted && visitorWon ? 700 : 400,
                  color:
                    isCompleted && visitorWon
                      ? "var(--text-primary)"
                      : "var(--text-muted)",
                  width: "60px",
                  textAlign: "center",
                }}
              >
                {game.visitor_score}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div style={{ flex: 1 }}>
              <div>
                <Link
                  to={`/leagues/${abbr}/teams/${game.home_team_id}`}
                  style={{
                    fontSize: "20px",
                    fontWeight: homeWon ? 700 : 500,
                    color: "var(--text-primary)",
                    textDecoration: "none",
                  }}
                >
                  <TeamDisplay
                    team={{
                      id: game.home_team_id,
                      name: game.home_team_name || "Home Team",
                      colour_code: game.home_team_colour_code,
                    }}
                  />
                </Link>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-muted)",
                    marginTop: "2px",
                  }}
                >
                  Home
                </div>
              </div>
            </div>
            {(isCompleted || isLive) && (
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: isCompleted && homeWon ? 700 : 400,
                  color:
                    isCompleted && homeWon
                      ? "var(--text-primary)"
                      : "var(--text-muted)",
                  width: "60px",
                  textAlign: "center",
                }}
              >
                {game.home_score}
              </div>
            )}
          </div>

          {/* The clock gets a row of its own rather than joining the date and
              badge below, which are a two-item row already. */}
          {isLive && (
            <div className="lp-gamehead-clock">
              <span className="lp-gamehead-clock-time" aria-label="Game clock">
                {formatClock(clockSeconds)}
              </span>
              <span className="lp-gamehead-clock-period">
                {periodLabel(
                  game.current_period,
                  regulationPeriodsOf(currentLeague?.period_minutes),
                )}
              </span>
              <LiveRefreshIndicator isRefreshing={isRefreshing} />
            </div>
          )}

          {/* Date and status share one line: the long-form date used to wrap
              on a phone and pushed the badge onto a line of its own. */}
          <div className="lp-gamehead-meta">
            <span className="lp-gamehead-date">
              {formatGameDateTimeShort(game.date)}
            </span>
            <span
              className="lp-badge"
              style={{
                backgroundColor: isLive
                  ? "var(--brand-accent)"
                  : isCompleted
                    ? "var(--brand-primary)"
                    : "#6c757d",
                color: "var(--bg-primary)",
                fontSize: "11px",
                padding: "3px 8px",
                flexShrink: 0,
              }}
            >
              {isLive ? "Live" : isCompleted ? "Final" : "Scheduled"}
            </span>
          </div>
        </div>
      </div>

      {/* Goals Section */}
      <div className="lp-game-details-grid">
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              <TeamDisplay
                team={{
                  id: game.visitor_team_id,
                  name: game.visitor_team_name || "Visitor Team",
                  colour_code: game.visitor_team_colour_code,
                }}
              />{" "}
              Goals
            </strong>
          </div>
          <div>
            {visitorGoals.length === 0 ? (
              <div className="lp-card-empty">No goals</div>
            ) : (
              visitorGoals.map((goal) => (
                <div
                  key={goal.id}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--text-secondary)",
                        minWidth: "50px",
                      }}
                    >
                      {formatPeriod(goal.period)} ·{" "}
                      {formatTime(goal.time, goal.time_formatted)}
                    </span>
                    <Link
                      to={`/leagues/${abbr}/players/${goal.goal_player_id}`}
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        textDecoration: "none",
                        marginLeft: "12px",
                      }}
                    >
                      #{goal.goal_player_jersey} {goal.goal_player_name}
                    </Link>
                  </div>
                  {(goal.assist1_player_id || goal.assist2_player_id) && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginLeft: "62px",
                      }}
                    >
                      Assists:{" "}
                      {goal.assist1_player_id && (
                        <Link
                          to={`/leagues/${abbr}/players/${goal.assist1_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{goal.assist1_player_jersey}{" "}
                          {goal.assist1_player_name}
                        </Link>
                      )}
                      {goal.assist1_player_id && goal.assist2_player_id && ", "}
                      {goal.assist2_player_id && (
                        <Link
                          to={`/leagues/${abbr}/players/${goal.assist2_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{goal.assist2_player_jersey}{" "}
                          {goal.assist2_player_name}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              <TeamDisplay
                team={{
                  id: game.home_team_id,
                  name: game.home_team_name || "Home Team",
                  colour_code: game.home_team_colour_code,
                }}
              />{" "}
              Goals
            </strong>
          </div>
          <div>
            {homeGoals.length === 0 ? (
              <div className="lp-card-empty">No goals</div>
            ) : (
              homeGoals.map((goal) => (
                <div
                  key={goal.id}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--text-secondary)",
                        minWidth: "50px",
                      }}
                    >
                      {formatPeriod(goal.period)} ·{" "}
                      {formatTime(goal.time, goal.time_formatted)}
                    </span>
                    <Link
                      to={`/leagues/${abbr}/players/${goal.goal_player_id}`}
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        textDecoration: "none",
                        marginLeft: "12px",
                      }}
                    >
                      #{goal.goal_player_jersey} {goal.goal_player_name}
                    </Link>
                  </div>
                  {(goal.assist1_player_id || goal.assist2_player_id) && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginLeft: "62px",
                      }}
                    >
                      Assists:{" "}
                      {goal.assist1_player_id && (
                        <Link
                          to={`/leagues/${abbr}/players/${goal.assist1_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{goal.assist1_player_jersey}{" "}
                          {goal.assist1_player_name}
                        </Link>
                      )}
                      {goal.assist1_player_id && goal.assist2_player_id && ", "}
                      {goal.assist2_player_id && (
                        <Link
                          to={`/leagues/${abbr}/players/${goal.assist2_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{goal.assist2_player_jersey}{" "}
                          {goal.assist2_player_name}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Penalties Section */}
      {(homePenalties.length > 0 || visitorPenalties.length > 0) && (
        <div className="lp-game-details-grid">
          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.visitor_team_id,
                    name: game.visitor_team_name || "Visitor Team",
                    colour_code: game.visitor_team_colour_code,
                  }}
                />{" "}
                Penalties
              </strong>
            </div>
            <div>
              {visitorPenalties.length === 0 ? (
                <div className="lp-card-empty">No penalties</div>
              ) : (
                visitorPenalties.map((penalty) => (
                  <div
                    key={penalty.id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          minWidth: "50px",
                        }}
                      >
                        {formatPeriod(penalty.period)} ·{" "}
                        {formatTime(
                          penalty.off_time,
                          penalty.off_time_formatted,
                        )}
                      </span>
                      {penalty.penalty_player_name ? (
                        <Link
                          to={`/leagues/${abbr}/players/${penalty.penalty_player_id}`}
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            marginLeft: "12px",
                          }}
                        >
                          #{penalty.penalty_player_jersey}{" "}
                          {penalty.penalty_player_name}
                        </Link>
                      ) : (
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            marginLeft: "12px",
                          }}
                        >
                          Player #{penalty.penalty_player_id}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginLeft: "62px",
                      }}
                    >
                      <strong>{penalty.penalty_length} min</strong> ·{" "}
                      {getPenaltyTypeLabel(
                        penalty.penalty_type_name,
                        penalty.penalty_type_abbreviation,
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.home_team_id,
                    name: game.home_team_name || "Home Team",
                    colour_code: game.home_team_colour_code,
                  }}
                />{" "}
                Penalties
              </strong>
            </div>
            <div>
              {homePenalties.length === 0 ? (
                <div className="lp-card-empty">No penalties</div>
              ) : (
                homePenalties.map((penalty) => (
                  <div
                    key={penalty.id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          minWidth: "50px",
                        }}
                      >
                        {formatPeriod(penalty.period)} ·{" "}
                        {formatTime(
                          penalty.off_time,
                          penalty.off_time_formatted,
                        )}
                      </span>
                      {penalty.penalty_player_name ? (
                        <Link
                          to={`/leagues/${abbr}/players/${penalty.penalty_player_id}`}
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            marginLeft: "12px",
                          }}
                        >
                          #{penalty.penalty_player_jersey}{" "}
                          {penalty.penalty_player_name}
                        </Link>
                      ) : (
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "var(--text-primary)",
                            marginLeft: "12px",
                          }}
                        >
                          Player #{penalty.penalty_player_id}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        marginLeft: "62px",
                      }}
                    >
                      <strong>{penalty.penalty_length} min</strong> ·{" "}
                      {getPenaltyTypeLabel(
                        penalty.penalty_type_name,
                        penalty.penalty_type_abbreviation,
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spares Section */}
      {(homeSpares.length > 0 || visitorSpares.length > 0) && (
        <div className="lp-game-details-grid" style={{ marginTop: "16px" }}>
          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.visitor_team_id,
                    name: game.visitor_team_name || "Visitor Team",
                    colour_code: game.visitor_team_colour_code,
                  }}
                />{" "}
                Spares
              </strong>
            </div>
            <div>
              {visitorSpares.length === 0 ? (
                <div className="lp-card-empty">No spares</div>
              ) : (
                visitorSpares.map((spare) => (
                  <div
                    key={spare.id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                      fontSize: "13px",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>
                      <Link
                        to={`/leagues/${abbr}/players/${spare.replaced_player_id}`}
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          textDecoration: "none",
                        }}
                      >
                        #{spare.replaced_player_jersey}{" "}
                        {spare.replaced_player_name}
                      </Link>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginLeft: "8px",
                        }}
                      >
                        Scratched
                      </span>
                    </div>
                    {spare.spare_player_id && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Replacement:{" "}
                        <Link
                          to={`/leagues/${abbr}/players/${spare.spare_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{spare.spare_player_jersey} {spare.spare_player_name}
                        </Link>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.home_team_id,
                    name: game.home_team_name || "Home Team",
                    colour_code: game.home_team_colour_code,
                  }}
                />{" "}
                Spares
              </strong>
            </div>
            <div>
              {homeSpares.length === 0 ? (
                <div className="lp-card-empty">No spares</div>
              ) : (
                homeSpares.map((spare) => (
                  <div
                    key={spare.id}
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                      fontSize: "13px",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>
                      <Link
                        to={`/leagues/${abbr}/players/${spare.replaced_player_id}`}
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "var(--text-primary)",
                          textDecoration: "none",
                        }}
                      >
                        #{spare.replaced_player_jersey}{" "}
                        {spare.replaced_player_name}
                      </Link>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginLeft: "8px",
                        }}
                      >
                        Scratched
                      </span>
                    </div>
                    {spare.spare_player_id && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Replacement:{" "}
                        <Link
                          to={`/leagues/${abbr}/players/${spare.spare_player_id}`}
                          style={{
                            color: "var(--text-secondary)",
                            textDecoration: "none",
                          }}
                        >
                          #{spare.spare_player_jersey} {spare.spare_player_name}
                        </Link>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rosters Section */}
      <div className="lp-game-details-grid" style={{ marginTop: "16px" }}>
        {/* Visitor Team Roster */}
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              <TeamDisplay
                team={{
                  id: game.visitor_team_id,
                  name: game.visitor_team_name || "Visitor Team",
                  colour_code: game.visitor_team_colour_code,
                }}
              />{" "}
              Roster
            </strong>
          </div>
          <div>
            {visitorPlayerStats.length === 0 ? (
              <div className="lp-card-empty">No roster</div>
            ) : (
              <table className="lp-table">
                <thead>
                  <tr>
                    <th className="lp-th-left" style={{ width: "60px" }}>
                      #
                    </th>
                    <th className="lp-th-left">Player</th>
                    <th>Pos</th>
                    <th title="Goals">G</th>
                    <th title="Assists">A</th>
                    <th title="Points">PTS</th>
                    <th title="Penalty Minutes">PIM</th>
                  </tr>
                </thead>
                <tbody>
                  {visitorPlayerStats.map((player) => {
                    const rowStyle: React.CSSProperties = {};
                    if (player.is_absent) {
                      rowStyle.opacity = 0.5;
                      rowStyle.textDecoration = "line-through";
                    }
                    if (player.is_spare) {
                      rowStyle.fontStyle = "italic";
                    }

                    return (
                      <tr key={player.player_id} style={rowStyle}>
                        <td className="lp-td-left">
                          <strong>{player.player_jersey || "-"}</strong>
                        </td>
                        <td className="lp-td-left">
                          <Link
                            to={`/leagues/${abbr}/players/${player.player_id}`}
                            style={{
                              color: "var(--text-primary)",
                              textDecoration: "none",
                              fontWeight: 500,
                            }}
                          >
                            {player.player_name}
                          </Link>
                          {player.is_spare && (
                            <span
                              style={{
                                fontSize: "10px",
                                marginLeft: "6px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              (Spare)
                            </span>
                          )}
                          {player.is_absent && (
                            <span
                              style={{
                                fontSize: "10px",
                                marginLeft: "6px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              (Absent)
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "12px",
                          }}
                        >
                          {player.position || "—"}
                        </td>
                        <td>{isScheduled ? "—" : player.goals}</td>
                        <td>{isScheduled ? "—" : player.assists}</td>
                        <td>{isScheduled ? "—" : player.points}</td>
                        <td>{isScheduled ? "—" : player.penalty_minutes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Home Team Roster */}
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px", color: "var(--text-primary)" }}>
              <TeamDisplay
                team={{
                  id: game.home_team_id,
                  name: game.home_team_name || "Home Team",
                  colour_code: game.home_team_colour_code,
                }}
              />{" "}
              Roster
            </strong>
          </div>
          <div>
            {homePlayerStats.length === 0 ? (
              <div className="lp-card-empty">No roster</div>
            ) : (
              <table className="lp-table">
                <thead>
                  <tr>
                    <th className="lp-th-left" style={{ width: "60px" }}>
                      #
                    </th>
                    <th className="lp-th-left">Player</th>
                    <th>Pos</th>
                    <th title="Goals">G</th>
                    <th title="Assists">A</th>
                    <th title="Points">PTS</th>
                    <th title="Penalty Minutes">PIM</th>
                  </tr>
                </thead>
                <tbody>
                  {homePlayerStats.map((player) => {
                    const rowStyle: React.CSSProperties = {};
                    if (player.is_absent) {
                      rowStyle.opacity = 0.5;
                      rowStyle.textDecoration = "line-through";
                    }
                    if (player.is_spare) {
                      rowStyle.fontStyle = "italic";
                    }

                    return (
                      <tr key={player.player_id} style={rowStyle}>
                        <td className="lp-td-left">
                          <strong>{player.player_jersey || "-"}</strong>
                        </td>
                        <td className="lp-td-left">
                          <Link
                            to={`/leagues/${abbr}/players/${player.player_id}`}
                            style={{
                              color: "var(--text-primary)",
                              textDecoration: "none",
                              fontWeight: 500,
                            }}
                          >
                            {player.player_name}
                          </Link>
                          {player.is_spare && (
                            <span
                              style={{
                                fontSize: "10px",
                                marginLeft: "6px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              (Spare)
                            </span>
                          )}
                          {player.is_absent && (
                            <span
                              style={{
                                fontSize: "10px",
                                marginLeft: "6px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              (Absent)
                            </span>
                          )}
                        </td>
                        <td
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "12px",
                          }}
                        >
                          {player.position || "—"}
                        </td>
                        <td>{isScheduled ? "—" : player.goals}</td>
                        <td>{isScheduled ? "—" : player.assists}</td>
                        <td>{isScheduled ? "—" : player.points}</td>
                        <td>{isScheduled ? "—" : player.penalty_minutes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Goalie Rosters Section */}
      {(visitorGoalieStats.length > 0 || homeGoalieStats.length > 0) && (
        <div className="lp-game-details-grid" style={{ marginTop: "16px" }}>
          {/* Visitor Team Goalies */}
          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.visitor_team_id,
                    name: game.visitor_team_name || "Visitor Team",
                    colour_code: game.visitor_team_colour_code,
                  }}
                />{" "}
                Goalies
              </strong>
            </div>
            <div>
              {visitorGoalieStats.length === 0 ? (
                <div className="lp-card-empty">No goalies</div>
              ) : (
                <table className="lp-table">
                  <thead>
                    <tr>
                      <th className="lp-th-left" style={{ width: "60px" }}>
                        #
                      </th>
                      <th className="lp-th-left">Player</th>
                      <th>Pos</th>
                      <th title="Goals Against">GA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitorGoalieStats.map((goalie) => {
                      const rowStyle: React.CSSProperties = {};
                      if (goalie.is_absent) {
                        rowStyle.opacity = 0.5;
                        rowStyle.textDecoration = "line-through";
                      }
                      if (goalie.is_spare) {
                        rowStyle.fontStyle = "italic";
                      }

                      return (
                        <tr key={goalie.player_id} style={rowStyle}>
                          <td className="lp-td-left">
                            <strong>{goalie.player_jersey || "-"}</strong>
                          </td>
                          <td className="lp-td-left">
                            <Link
                              to={`/leagues/${abbr}/players/${goalie.player_id}`}
                              style={{
                                color: "var(--text-primary)",
                                textDecoration: "none",
                                fontWeight: 500,
                              }}
                            >
                              {goalie.player_name}
                            </Link>
                            {goalie.is_spare && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  marginLeft: "6px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                (Spare)
                              </span>
                            )}
                            {goalie.is_absent && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  marginLeft: "6px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                (Absent)
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "12px",
                            }}
                          >
                            {goalie.position || "—"}
                          </td>
                          <td>{isScheduled ? "—" : goalie.goals_against}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Home Team Goalies */}
          <div className="lp-card">
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid var(--border-medium)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <strong
                style={{ fontSize: "13px", color: "var(--text-primary)" }}
              >
                <TeamDisplay
                  team={{
                    id: game.home_team_id,
                    name: game.home_team_name || "Home Team",
                    colour_code: game.home_team_colour_code,
                  }}
                />{" "}
                Goalies
              </strong>
            </div>
            <div>
              {homeGoalieStats.length === 0 ? (
                <div className="lp-card-empty">No goalies</div>
              ) : (
                <table className="lp-table">
                  <thead>
                    <tr>
                      <th className="lp-th-left" style={{ width: "60px" }}>
                        #
                      </th>
                      <th className="lp-th-left">Player</th>
                      <th>Pos</th>
                      <th title="Goals Against">GA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {homeGoalieStats.map((goalie) => {
                      const rowStyle: React.CSSProperties = {};
                      if (goalie.is_absent) {
                        rowStyle.opacity = 0.5;
                        rowStyle.textDecoration = "line-through";
                      }
                      if (goalie.is_spare) {
                        rowStyle.fontStyle = "italic";
                      }

                      return (
                        <tr key={goalie.player_id} style={rowStyle}>
                          <td className="lp-td-left">
                            <strong>{goalie.player_jersey || "-"}</strong>
                          </td>
                          <td className="lp-td-left">
                            <Link
                              to={`/leagues/${abbr}/players/${goalie.player_id}`}
                              style={{
                                color: "var(--text-primary)",
                                textDecoration: "none",
                                fontWeight: 500,
                              }}
                            >
                              {goalie.player_name}
                            </Link>
                            {goalie.is_spare && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  marginLeft: "6px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                (Spare)
                              </span>
                            )}
                            {goalie.is_absent && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  marginLeft: "6px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                (Absent)
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              color: "var(--text-secondary)",
                              fontSize: "12px",
                            }}
                          >
                            {goalie.position || "—"}
                          </td>
                          <td>{isScheduled ? "—" : goalie.goals_against}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
